import { useState } from 'react';
import api from '../services/api';
import { errorMessage } from '../lib/http';
import { PAYMENT_STATUS, formatDateTime, formatMoney } from '../lib/format';
import { BTN_PRIMARY, BTN_PRIMARY_STYLE, BTN_SOFT, BTN_SOFT_STYLE } from '../lib/ui';
import type { Payment } from '../types/workspace';
import { Spinner, StatusPill } from './ui';

/** Step 3 · Rated — dedicated rating-fee workspace (replaces overview banners). */
export default function RatingFeePanel({
  companyId,
  fee,
  publishedVersion,
  hasDraft = false,
  onChanged,
  onError,
  onContinue,
}: {
  companyId: string;
  fee: Payment;
  publishedVersion?: number | null;
  hasDraft?: boolean;
  onChanged: (message?: string) => void;
  onError: (message: string) => void;
  onContinue?: () => void;
}) {
  const [busy, setBusy] = useState<'paid' | 'waive' | null>(null);
  const paid = fee.status === 'PAID';
  const st = PAYMENT_STATUS[fee.status] || { label: fee.status, tone: 'slate' as const };
  const awaitingPublish = paid && !publishedVersion;

  const act = async (action: 'MARK_PAID' | 'CANCEL') => {
    const reference = action === 'MARK_PAID' ? window.prompt('Bank transfer / UTR reference (optional)') : null;
    if (action === 'MARK_PAID' && reference === null) return;
    if (action === 'CANCEL' && !window.confirm(`Waive rating fee ${formatMoney(fee.amount, fee.currency)}?`)) return;
    setBusy(action === 'MARK_PAID' ? 'paid' : 'waive');
    try {
      await api.patch(`/admin/companies/${companyId}/payments/${fee.id}`, { action, reference: reference || null });
      onChanged(action === 'MARK_PAID' ? 'Rating fee marked paid — company can view the rating.' : 'Rating fee waived.');
    } catch (err) {
      onError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <section
      className="mz-fade-up overflow-hidden rounded-3xl"
      style={{
        border: '1px solid #f9a8d4',
        background: 'linear-gradient(145deg, #7a0038 0%, #c4005e 42%, #ff6aad 100%)',
        boxShadow: '0 22px 48px rgba(214,0,110,0.28)',
      }}
    >
      <div className="grid gap-0 lg:grid-cols-[1.15fr_0.85fr]">
        <div className="p-6 text-white sm:p-8">
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-white/70">Step 3 · Rated</p>
          <h2 className="mt-2 text-3xl font-black tracking-tight sm:text-4xl">
            {awaitingPublish
              ? 'Payment received — publish the rating'
              : paid
                ? 'Rating fee settled'
                : 'Unlock the rating for the company'}
          </h2>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-white/85">
            {awaitingPublish
              ? hasDraft
                ? 'The company already paid. Review the draft below and publish it to move this workspace to Step 3 (Rated).'
                : 'The company already paid in Musb. Generate and publish a rating draft below to complete Step 3 — payment alone does not advance the journey.'
              : paid
                ? 'Payment is complete. The company can view the published rating, update documents if needed, and submit again for review.'
                : publishedVersion
                  ? `Rating v${publishedVersion} is published. The company cannot see it until this fee is paid (or waived).`
                  : 'Collect the rating fee before the company opens the published result.'}
          </p>

          <div className="mt-6 flex flex-wrap items-end gap-4">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-white/65">Amount due</p>
              <p className="text-4xl font-black tracking-tight">{formatMoney(fee.amount, fee.currency)}</p>
            </div>
            <StatusPill label={st.label} tone={st.tone} />
          </div>

          <ul className="mt-6 space-y-2 text-sm text-white/90">
            <li className="flex gap-2">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-white/80" />
              Company pays via Musb (UPI, cards, netbanking) or you mark a bank transfer here.
            </li>
            <li className="flex gap-2">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-white/80" />
              After payment, they can replace documents and submit for a fresh review — no separate Improve step.
            </li>
            <li className="flex gap-2">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-white/80" />
              Mode: {fee.mode === 'DOMESTIC' ? 'India · all Razorpay methods' : 'International card'}
            </li>
          </ul>
        </div>

        <div className="bg-white/95 p-6 sm:p-8" style={{ backdropFilter: 'blur(8px)' }}>
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#c084a0]">Payment record</p>
          <p className="mt-1 text-sm font-bold text-[#6b0030]">{fee.description}</p>
          <dl className="mt-4 space-y-3 text-sm">
            <div className="flex justify-between gap-3 border-b border-[#fce7f3] pb-2">
              <dt className="text-[#c084a0]">Status</dt>
              <dd className="font-semibold text-[#6b0030]">{st.label}</dd>
            </div>
            <div className="flex justify-between gap-3 border-b border-[#fce7f3] pb-2">
              <dt className="text-[#c084a0]">Created</dt>
              <dd className="font-semibold text-[#6b0030]">{formatDateTime(fee.createdAt)}</dd>
            </div>
            {fee.paidAt && (
              <div className="flex justify-between gap-3 border-b border-[#fce7f3] pb-2">
                <dt className="text-[#c084a0]">Paid</dt>
                <dd className="font-semibold text-[#6b0030]">{formatDateTime(fee.paidAt)}</dd>
              </div>
            )}
            {fee.providerPaymentId && (
              <div className="flex justify-between gap-3 border-b border-[#fce7f3] pb-2">
                <dt className="text-[#c084a0]">Reference</dt>
                <dd className="truncate font-semibold text-[#6b0030]">{fee.providerPaymentId}</dd>
              </div>
            )}
            {fee.failureReason && fee.status !== 'PAID' && (
              <div className="rounded-xl px-3 py-2 text-xs text-[#be123c]" style={{ background: '#fff1f2', border: '1px solid #fecdd3' }}>
                Last attempt: {fee.failureReason}
              </div>
            )}
          </dl>

          <div className="mt-6 flex flex-col gap-2">
            {!paid ? (
              <>
                <button type="button" disabled={busy !== null} onClick={() => void act('MARK_PAID')} className={`${BTN_PRIMARY} w-full py-3`} style={BTN_PRIMARY_STYLE}>
                  {busy === 'paid' && <Spinner />} Mark fee as paid
                </button>
                <button type="button" disabled={busy !== null} onClick={() => void act('CANCEL')} className={`${BTN_SOFT} w-full`} style={BTN_SOFT_STYLE}>
                  {busy === 'waive' && <Spinner />} Waive fee
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={onContinue}
                className={`${BTN_PRIMARY} w-full py-3`}
                style={BTN_PRIMARY_STYLE}
              >
                {awaitingPublish
                  ? hasDraft
                    ? 'Review draft & publish →'
                    : 'Generate rating draft below →'
                  : 'Continue to rating report →'}
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
